import { useState, useCallback, useRef } from 'react';

/**
 * Custom hook for making API calls with loading and error handling
 * @returns {Object} API call state and execute function
 */
const useApiCall = () => {
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [data, setData] = useState(null);
  const abortControllerRef = useRef(null);

  // Execute API call
  const execute = useCallback(async (apiFunction, ...args) => {
    // Prevent multiple simultaneous calls
    if (isLoading) {
      return { success: false, error: 'Request already in progress' };
    }

    // Clear previous error
    setError(null);
    setIsLoading(true);

    // Create abort controller for cancellation
    abortControllerRef.current = new AbortController();

    try {
      const result = await apiFunction(...args);

      if (result.success) {
        setData(result.data);
        setError(null);
        return { success: true, data: result.data };
      } else {
        const errorMessage = result.error || 'An error occurred';
        setError(errorMessage);
        return { success: false, error: errorMessage };
      }
    } catch (err) {
      console.error('API call error:', err);

      // Handle abort
      if (err.name === 'AbortError') {
        return { success: false, error: 'Request cancelled' };
      }

      // Extract error message
      const errorMessage =
        err.response?.data?.error ||
        err.message ||
        'An unexpected error occurred';

      setError(errorMessage);
      return { success: false, error: errorMessage };
    } finally {
      setIsLoading(false);
      abortControllerRef.current = null;
    }
  }, [isLoading]);

  // Cancel ongoing request
  const cancel = useCallback(() => {
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }
  }, []);

  // Reset state
  const reset = useCallback(() => {
    setIsLoading(false);
    setError(null);
    setData(null);
    cancel();
  }, [cancel]);

  // Clear error
  const clearError = useCallback(() => {
    setError(null);
  }, []);

  return {
    isLoading,
    error,
    data,
    execute,
    cancel,
    reset,
    clearError,
  };
};

export default useApiCall;
